import styles from './CheckoutInfoPage.scss';

import cx from 'classnames';
import { Form, Formik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useCart } from 'src/components/Cart/UseCart';
import { CheckoutRoutePath } from 'src/components/Checkout/Checkout';
import {
  businessPattern,
  cityPattern,
  namePattern,
  streetAddressPattern,
  usZipCodePattern,
} from 'src/components/Contact/regex';
import { FormWrapper } from 'src/components/Form/FormWrapper';
import { useSalesChannel } from 'src/components/SalesChannel/SalesChannel';
import { TextV2 } from 'src/components/TextV2/TextV2';
import { redesignV2RoutePath } from 'src/constants/redesignV2';
import { getTaxRate } from 'src/services/shop/checkout';
import { getField } from 'src/utils/EntryUtil';
import { LocalStorage } from 'src/utils/LocalStorage';
import { useCheckout } from '../CheckoutProvider';
import { CHECKOUT_LOCAL_STORAGE_KEY } from '../UseCheckout';
import {
  AddressLine1Field,
  AddressLine2Field,
  BusinessNameField,
  CityField,
  FirstNameField,
  LastNameField,
  StateField,
  ZipCodeField
} from './Fields';

export interface CheckoutInfoFields {
  firstName: string;
  lastName: string;
  address1: string;
  address2: string;
  businessName: string;
  city: string;
  state: string;
  zip: string;
}

const formRequiredErrorMessages = {
  firstName: 'firstName.required',
  lastName: 'lastName.required',
  businessName: 'businessName.required',
  address1: 'address1.required',
  city: 'city.required',
  state: 'state.required',
  zip: 'zip.required',
};

export interface FormErrors {
  firstName?: string;
  lastName?: string;
  address1?: string;
  address2?: string;
  businessName?: string;
  city?: string;
  state?: string;
  zip?: string;
}

const DEFAULT_USER_INFO = {
  firstName: '',
  lastName: '',
  address1: '',
  address2: '',
  businessName: '',
  city: '',
  zip: '',
  state: '',
};

const ADDRESS_MAX_LENGTH = 24;

export const CheckoutInfoPage = React.forwardRef<HTMLDivElement>(({ }, ref) => {
  const history = useHistory();
  const { setUserInfoValid, userInfo, setUserInfo, continueButtonRef, setIsLoading, setHasApiError } = useCheckout();
  const { salesChannel } = useSalesChannel();
  const { setTaxRate } = useCart();
  const resellerId = getField(salesChannel, 'cloverResellerId');
  const requiredFields = [
    'firstName',
    'lastName',
    'businessName',
    'address1',
    'city',
    'state',
    'zip',
  ];

  const { t } = useTranslation();

  const validate = (values: CheckoutInfoFields) => {
    const errors: FormErrors = {};
    requiredFields.forEach(field => {
      if (!values[field]) {
        errors[field] = t(formRequiredErrorMessages[field]);
      }
    });

    if (values.firstName && !namePattern.test(values.firstName)) {
      errors.firstName = t('firstName.invalid');
    }

    if (values.lastName && !namePattern.test(values.lastName)) {
      errors.lastName = t('lastName.invalid');
    }

    if (values.businessName && !businessPattern.test(values.businessName)) {
      errors.businessName = t('businessName.invalid');
    }

    if (values.address1) {
      if (!streetAddressPattern.test(values.address1)) {
        errors.address1 = t('streetAddress.invalid');
      } else if (values.address1.length > ADDRESS_MAX_LENGTH) {
        errors.address1 = t('streetAddress.length', { maxlen: ADDRESS_MAX_LENGTH });
      }
    }

    if (values.address2) {
      if (!streetAddressPattern.test(values.address2)) {
        errors.address2 = t('streetAddress.invalid');
      } else if (values.address2.length > ADDRESS_MAX_LENGTH) {
        errors.address2 = t('streetAddress.length', { maxlen: ADDRESS_MAX_LENGTH });
      }
    }

    if (values.zip && !usZipCodePattern.test(values.zip)) {
      errors.zip = t('zip.invalid');
    }

    if (values.city && !cityPattern.test(values.city)) {
      errors.city = t('city.invalid');
    }

    const hasErrors = Object.values(errors).some(error => !!error);

    setUserInfoValid(!hasErrors);

    return errors;
  };

  const onSubmit = async (values: CheckoutInfoFields) => {
    setIsLoading(true);
    setUserInfo(values);
    const address = {
      city: values.city,
      zip: values.zip,
    };

    try {
      if (!resellerId) {
        throw new Error('Reseller ID not found');
      }

      const taxRate = await getTaxRate({ resellerId, address });
      setTaxRate(taxRate);

      LocalStorage.trySetJSONItem(CHECKOUT_LOCAL_STORAGE_KEY, values);
      const reviewPage = redesignV2RoutePath.PURCHASE + CheckoutRoutePath.REVIEW;
      history.push(reviewPage);
    } catch {
      setHasApiError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const initialUserInfo = userInfo || DEFAULT_USER_INFO;

  return (
    <section className={styles.checkoutInfoPage}>
      <header className={styles.checkoutInfoPageHeader}>
        <TextV2
          className={styles.checkoutInfoPageHeading}
          tagName="h2"
          variant="h2-bold"
          value={t('checkout-info-page.title')}
        />
        <TextV2
          className={styles.checkoutInfoPageDescription}
          tagName="p"
          variant="body"
          value={t('checkout-info-page.description')}
        />
      </header>
      <FormWrapper>
        <Formik
          initialValues={initialUserInfo}
          validate={validate}
          onSubmit={onSubmit}
          validateOnBlur
          validateOnMount
        >
          <div ref={ref} data-test="checkout-info-form-container">
            <Form className={styles.form}>
              <div className={cx(styles.formRow, styles.formRowCols2)}>
                <div className={styles.formField}>
                  <FirstNameField />
                </div>
                <div className={styles.formField}>
                  <LastNameField />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formField}>
                  <BusinessNameField />
                </div>
              </div>
              <div className={cx(styles.formRow, styles.formRowShipping)}>
                <div className={styles.formField}>
                  <AddressLine1Field />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formField}>
                  <AddressLine2Field />
                </div>
              </div>
              <div className={cx(styles.formRow, styles.formRowCols3)}>
                <div className={styles.formField}>
                  <CityField />
                </div>
                <div className={styles.formField}>
                  <StateField />
                </div>
                <div className={styles.formField}>
                  <ZipCodeField />
                </div>
              </div>
              <button type="submit" ref={continueButtonRef} className={styles.formSubmitBtn}>{t('continue')}</button>
            </Form>
          </div>
        </Formik>
      </FormWrapper>
    </section>
  );
});
